import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useKidAuth } from '../../contexts/KidAuthContext';
import { dbKidData as db } from '../../services/supabase';
import { parseCurrency } from '../../utils/currency';
import '../../styles/brand-typography.css';
import './KidMoneyFlow.css';
import KidLayout from '../../components/Layout/KidLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';

const AddIncome = () => {
  const navigate = useNavigate();
  const {
    currentKid,
    isFamilyInactive,
    canWrite: familyAccessCanWrite,
  } = useKidAuth();

  const isWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;
  const [selectedType, setSelectedType] = useState('');
  const [formData, setFormData] = useState({
    amount: '',
    note: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isWriteLocked) {
      setError(
        'Your MyPiggy is currently read-only.'
      );
      return;
    }
    setLoading(true);
    setError('');

    try {
      const amountCents = parseCurrency(formData.amount);

      if (amountCents <= 0) {
        throw new Error('Amount must be greater than $0');
      }

      // Regular kid-entered income does not require parent approval.
      // Chore rewards are handled only through Kid Chores -> complete_kid_chore RPC.
      await db.createTransaction({
        family_id: currentKid.family_id,
        child_id: currentKid.id,
        type: 'income',
        amount_cents: amountCents,
        subtype: selectedType,
        note: formData.note || undefined,
        approval_status: 'approved',
        created_by: 'child'
      });

      navigate('/kid/dashboard');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!currentKid) {
    return null;
  }

  return (
    <KidLayout
      className="mypiggy-brand kid-money-flow"
      title="Add Money"
      avatar={currentKid.avatar}
      showBack
    >
      <div className="space-y-6">
        {!selectedType ? (
          <Card className="p-6">
            <h2 className="text-xl font-extrabold text-center mb-6">
              How did you get money?
            </h2>

            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <Button
                variant="kid"
                className="income-choice"
                onClick={() => setSelectedType('allowance')}
              >
                <MyPiggyIcon name="allowance" size="md" />
                <span>Allowance</span>
              </Button>

              <Button
                variant="kid"
                className="income-choice"
                onClick={() => setSelectedType('gift')}
              >
                <MyPiggyIcon name="gift-money" size="md" />
                <span>Gift</span>
              </Button>

              <Button
                variant="kid"
                className="income-choice"
                onClick={() => setSelectedType('bonus')}
              >
                <MyPiggyIcon name="money-received" size="md" />
                <span>Bonus</span>
              </Button>
            </div>

            <div className="mt-4 rounded-xl border border-purple-100 bg-purple-50 p-4 text-base font-bold leading-relaxed text-purple-900">
              Finished a chore? Mark it complete in <Link to="/kid/chores" className="whitespace-nowrap text-lg font-extrabold text-teal-800 underline underline-offset-4 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-800">Do Chores</Link>. Your parent may need to approve it before the reward is added.
            </div>
          </Card>
        ) : (
          <Card className="p-6">
            <h2 className="text-xl font-extrabold text-center mb-6">
              Add {selectedType.charAt(0).toUpperCase() + selectedType.slice(1)}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Amount"
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0.01"
                required
                className="text-lg"
              />

              <Input
                label="Note (optional)"
                name="note"
                value={formData.note}
                onChange={handleChange}
                placeholder="What was this for?"
              />

              {error && (
                <p className="text-red-500 text-center">{error}</p>
              )}

              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setSelectedType('');
                    setFormData({ amount: '', note: '' });
                    setError('');
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="kid"
                  className="flex-1"
                  disabled={
                    loading ||
                    isWriteLocked
                  }
                >
                  {loading ? 'Adding...' : 'Add Money'}
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>
    </KidLayout>
  );
};

export default AddIncome;

